export const WORLD_SEED = 734_021;
/** Phase 9 deterministic movement bounds: 8 km × 8 km. */
import { WORLD_HALF_EXTENT_METERS } from "./world-bounds.js";
export const WORLD_HALF_SIZE = WORLD_HALF_EXTENT_METERS;
/** Authored Phase 1–8 macro-site retained around the origin. */
export const START_AREA_HALF_SIZE = 160;
import { TERRAIN_CHUNK_SIZE } from "./world-bounds.js";
export { TERRAIN_CHUNK_SIZE } from "./world-bounds.js";
export const TERRAIN_VERTEX_SPACING = 1;
import { PLAYER_RADIUS, resolveStaticCollider } from "./static-collision.js";
export { PLAYER_RADIUS, resolveStaticCollider } from "./static-collision.js";
export const GEOLOGY_ASSET_IDS = Object.freeze([
    "rock_small_01",
    "rock_small_02",
    "rock_medium_01",
    "rock_medium_02",
    "boulder_large_01",
    "boulder_large_02",
    "rock_formation_01",
]);
export const GEOLOGY_ASSETS = Object.freeze({
    rock_small_01: { id: "rock_small_01", category: "small", collisionRadius: 0, boundsRadius: 0.42 },
    rock_small_02: { id: "rock_small_02", category: "small", collisionRadius: 0, boundsRadius: 0.48 },
    rock_medium_01: { id: "rock_medium_01", category: "medium", collisionRadius: 0, boundsRadius: 0.9 },
    rock_medium_02: { id: "rock_medium_02", category: "medium", collisionRadius: 0, boundsRadius: 1.05 },
    boulder_large_01: { id: "boulder_large_01", category: "boulder", collisionRadius: 1.02, boundsRadius: 1.72 },
    boulder_large_02: { id: "boulder_large_02", category: "boulder", collisionRadius: 1.18, boundsRadius: 1.95 },
    rock_formation_01: { id: "rock_formation_01", category: "formation", collisionRadius: 2.7, boundsRadius: 4.6 },
});
export const VEGETATION_ASSET_IDS = Object.freeze([
    "tree_dead_01",
    "tree_dead_02",
    "tree_dead_03",
    "tree_burned_01",
    "tree_broken_01",
    "tree_sparse_01",
    "shrub_dead_01",
    "shrub_dead_02",
    "living_tree_01",
    "living_tree_02",
    "nuclear_tree_tall_01",
    "nuclear_tree_tall_02",
    "nuclear_tree_massive_01",
    "nuclear_tree_dead_giant_01",
    "fallen_log_large_01",
    "shrub_living_01",
    "grass_cluster_01",
    "forest_understory_cluster_01",
]);
export const VEGETATION_ASSETS = Object.freeze({
    tree_dead_01: { id: "tree_dead_01", category: "dead_tree", collisionRadius: 0.42, boundsCenterY: 4.15, boundsRadius: 4.9 },
    tree_dead_02: { id: "tree_dead_02", category: "dead_tree", collisionRadius: 0.5, boundsCenterY: 3.75, boundsRadius: 4.7 },
    tree_dead_03: { id: "tree_dead_03", category: "dead_tree", collisionRadius: 0.48, boundsCenterY: 3.9, boundsRadius: 5.2 },
    tree_burned_01: { id: "tree_burned_01", category: "burned_tree", collisionRadius: 0.46, boundsCenterY: 2.9, boundsRadius: 3.55 },
    tree_broken_01: { id: "tree_broken_01", category: "broken_tree", collisionRadius: 0.52, boundsCenterY: 3.25, boundsRadius: 4.35 },
    tree_sparse_01: { id: "tree_sparse_01", category: "sparse_tree", collisionRadius: 0.44, boundsCenterY: 3.8, boundsRadius: 4.8 },
    shrub_dead_01: { id: "shrub_dead_01", category: "dead_shrub", collisionRadius: 0, boundsCenterY: 0.65, boundsRadius: 1.15 },
    shrub_dead_02: { id: "shrub_dead_02", category: "dead_shrub", collisionRadius: 0, boundsCenterY: 0.8, boundsRadius: 1.35 },
    living_tree_01: { id: "living_tree_01", category: "living_tree", collisionRadius: 0.48, boundsCenterY: 6.1, boundsRadius: 7.4 },
    living_tree_02: { id: "living_tree_02", category: "living_tree", collisionRadius: 0.56, boundsCenterY: 7.2, boundsRadius: 8.4 },
    nuclear_tree_tall_01: { id: "nuclear_tree_tall_01", category: "nuclear_tree", collisionRadius: 0.72, boundsCenterY: 12, boundsRadius: 13.8 },
    nuclear_tree_tall_02: { id: "nuclear_tree_tall_02", category: "nuclear_tree", collisionRadius: 0.82, boundsCenterY: 14, boundsRadius: 16 },
    nuclear_tree_massive_01: { id: "nuclear_tree_massive_01", category: "nuclear_tree", collisionRadius: 1.15, boundsCenterY: 15, boundsRadius: 17.5 },
    nuclear_tree_dead_giant_01: { id: "nuclear_tree_dead_giant_01", category: "dead_giant", collisionRadius: 0.95, boundsCenterY: 12.5, boundsRadius: 14.2 },
    fallen_log_large_01: { id: "fallen_log_large_01", category: "fallen_log", collisionRadius: 0.7, boundsCenterY: 0.8, boundsRadius: 8.4 },
    shrub_living_01: { id: "shrub_living_01", category: "living_shrub", collisionRadius: 0, boundsCenterY: 0.95, boundsRadius: 1.7 },
    grass_cluster_01: { id: "grass_cluster_01", category: "grass", collisionRadius: 0, boundsCenterY: 0.42, boundsRadius: 1.1 },
    forest_understory_cluster_01: { id: "forest_understory_cluster_01", category: "understory", collisionRadius: 0, boundsCenterY: 1.1, boundsRadius: 2.2 },
});
export const HUMAN_ASSET_IDS = Object.freeze([
    "ruined_concrete_wall_01",
    "ruined_concrete_wall_02",
    "ruined_corner_01",
    "ruined_entry_01",
    "collapsed_slab_01",
    "small_bunker_01",
    "lamp_post_broken_01",
    "industrial_lamp_surviving_01",
    "wreck_worklight_01",
    "utility_pole_01",
    "road_sign_01",
    "fence_segment_01",
    "concrete_barrier_01",
    "wreck_car_01",
    "industrial_tank_01",
    "concrete_rubble_01",
    "concrete_rubble_02",
    "metal_debris_01",
    "road_damage_01",
    "highway_lane_marking_01",
    "parking_lot_01",
    "road_access_lane_01",
    "house_ruin_01",
    "house_ruin_02",
    "small_shop_ruin_01",
    "garage_ruin_01",
    "apartment_shell_01",
    "commercial_shell_01",
    "factory_hall_01",
    "warehouse_01",
    "smokestack_01",
    "industrial_pipe_run_01",
    "storage_tank_group_01",
    "substation_01",
    "highway_sign_01",
    "guardrail_01",
    "gas_station_canopy_01",
    "fuel_pump_01",
    "convenience_store_shell_01",
    "wreck_truck_01",
    "greenhouse_01",
    "irrigation_pipe_01",
    "farm_water_tank_01",
    "farm_service_building_01",
    "crop_row_01",
    "supply_cache_closed",
    "supply_cache_open",
]);
export const HUMAN_ASSETS = Object.freeze({
    ruined_concrete_wall_01: {
        id: "ruined_concrete_wall_01",
        category: "structure",
        collision: { shape: "box", halfWidth: 2.55, halfDepth: 0.26 },
        boundsCenterY: 1.5263,
        boundsRadius: 2.9808,
    },
    ruined_concrete_wall_02: {
        id: "ruined_concrete_wall_02",
        category: "structure",
        collision: { shape: "box", halfWidth: 2.5, halfDepth: 0.27 },
        boundsCenterY: 1.4525,
        boundsRadius: 2.9013,
    },
    ruined_corner_01: {
        id: "ruined_corner_01",
        category: "structure",
        collision: { shape: "box", halfWidth: 2.05, halfDepth: 2.05 },
        boundsCenterY: 1.5909,
        boundsRadius: 3.1428,
    },
    ruined_entry_01: {
        id: "ruined_entry_01",
        category: "structure",
        collision: { shape: "box", halfWidth: 2.6, halfDepth: 0.55 },
        boundsCenterY: 1.7716,
        boundsRadius: 3.2097,
    },
    collapsed_slab_01: {
        id: "collapsed_slab_01",
        category: "structure",
        collision: { shape: "none" },
        boundsCenterY: 0.2416,
        boundsRadius: 2.6039,
    },
    small_bunker_01: {
        id: "small_bunker_01",
        category: "structure",
        collision: { shape: "box", halfWidth: 2.9, halfDepth: 2.25 },
        boundsCenterY: 1.1699,
        boundsRadius: 4.1546,
    },
    lamp_post_broken_01: {
        id: "lamp_post_broken_01",
        category: "infrastructure",
        collision: { shape: "circle", radius: 0.2 },
        boundsCenterY: 2.708,
        boundsRadius: 3.084,
    },
    industrial_lamp_surviving_01: {
        id: "industrial_lamp_surviving_01",
        category: "infrastructure",
        collision: { shape: "circle", radius: 0.3 },
        boundsCenterY: 1.785,
        boundsRadius: 2.2242,
    },
    wreck_worklight_01: {
        id: "wreck_worklight_01",
        category: "vehicle",
        collision: { shape: "box", halfWidth: 1.05, halfDepth: 1.85 },
        boundsCenterY: 0.7748,
        boundsRadius: 2.0844,
    },
    utility_pole_01: {
        id: "utility_pole_01",
        category: "infrastructure",
        collision: { shape: "circle", radius: 0.29 },
        boundsCenterY: 3.9408,
        boundsRadius: 3.9772,
    },
    road_sign_01: {
        id: "road_sign_01",
        category: "infrastructure",
        collision: { shape: "circle", radius: 0.17 },
        boundsCenterY: 1.6668,
        boundsRadius: 1.6798,
    },
    fence_segment_01: {
        id: "fence_segment_01",
        category: "infrastructure",
        collision: { shape: "box", halfWidth: 2.55, halfDepth: 0.12 },
        boundsCenterY: 1.023,
        boundsRadius: 2.7971,
    },
    concrete_barrier_01: {
        id: "concrete_barrier_01",
        category: "infrastructure",
        collision: { shape: "box", halfWidth: 1.5, halfDepth: 0.48 },
        boundsCenterY: 0.5479,
        boundsRadius: 1.7236,
    },
    wreck_car_01: {
        id: "wreck_car_01",
        category: "vehicle",
        collision: { shape: "box", halfWidth: 1.05, halfDepth: 2.28 },
        boundsCenterY: 0.7961,
        boundsRadius: 2.5175,
    },
    industrial_tank_01: {
        id: "industrial_tank_01",
        category: "industrial",
        collision: { shape: "capsule", radius: 1.45, halfLength: 2.55 },
        boundsCenterY: 1.2569,
        boundsRadius: 4.3475,
    },
    concrete_rubble_01: {
        id: "concrete_rubble_01",
        category: "rubble",
        collision: { shape: "none" },
        boundsCenterY: 0.421,
        boundsRadius: 1.5519,
    },
    concrete_rubble_02: {
        id: "concrete_rubble_02",
        category: "rubble",
        collision: { shape: "none" },
        boundsCenterY: 0.28,
        boundsRadius: 1.633,
    },
    metal_debris_01: {
        id: "metal_debris_01",
        category: "rubble",
        collision: { shape: "none" },
        boundsCenterY: 0.389,
        boundsRadius: 1.6465,
    },
    road_damage_01: {
        id: "road_damage_01",
        category: "road_damage",
        collision: { shape: "none" },
        boundsCenterY: 0.0195,
        boundsRadius: 2.6867,
    },
    highway_lane_marking_01: { id: "highway_lane_marking_01", category: "infrastructure", collision: { shape: "none" }, boundsCenterY: 0.0373, boundsRadius: 8.8193 },
    parking_lot_01: { id: "parking_lot_01", category: "infrastructure", collision: { shape: "none" }, boundsCenterY: 0.035, boundsRadius: 16.6434 },
    road_access_lane_01: { id: "road_access_lane_01", category: "infrastructure", collision: { shape: "none" }, boundsCenterY: 0.036, boundsRadius: 17.8886 },
    house_ruin_01: { id: "house_ruin_01", category: "structure", collision: { shape: "box", halfWidth: 5, halfDepth: 4 }, boundsCenterY: 2.4389, boundsRadius: 7.0203 },
    house_ruin_02: { id: "house_ruin_02", category: "structure", collision: { shape: "box", halfWidth: 4.4, halfDepth: 3.6 }, boundsCenterY: 3.1792, boundsRadius: 6.6504 },
    small_shop_ruin_01: { id: "small_shop_ruin_01", category: "structure", collision: { shape: "box", halfWidth: 6.5, halfDepth: 4.8 }, boundsCenterY: 2.4419, boundsRadius: 8.6039 },
    garage_ruin_01: { id: "garage_ruin_01", category: "structure", collision: { shape: "box", halfWidth: 5.2, halfDepth: 4.4 }, boundsCenterY: 2.2552, boundsRadius: 7.329 },
    apartment_shell_01: { id: "apartment_shell_01", category: "structure", collision: { shape: "box", halfWidth: 7, halfDepth: 6 }, boundsCenterY: 7.5, boundsRadius: 11.8849 },
    commercial_shell_01: { id: "commercial_shell_01", category: "structure", collision: { shape: "box", halfWidth: 9, halfDepth: 7 }, boundsCenterY: 4.5926, boundsRadius: 12.292 },
    factory_hall_01: { id: "factory_hall_01", category: "industrial", collision: { shape: "box", halfWidth: 15, halfDepth: 9 }, boundsCenterY: 6.8217, boundsRadius: 18.7687 },
    warehouse_01: { id: "warehouse_01", category: "industrial", collision: { shape: "box", halfWidth: 13, halfDepth: 8 }, boundsCenterY: 5.1321, boundsRadius: 16.104 },
    smokestack_01: { id: "smokestack_01", category: "industrial", collision: { shape: "circle", radius: 2.6 }, boundsCenterY: 15.4925, boundsRadius: 15.7345 },
    industrial_pipe_run_01: { id: "industrial_pipe_run_01", category: "industrial", collision: { shape: "box", halfWidth: 8, halfDepth: 1.8 }, boundsCenterY: 2.1, boundsRadius: 8.2196 },
    storage_tank_group_01: { id: "storage_tank_group_01", category: "industrial", collision: { shape: "box", halfWidth: 5.5, halfDepth: 4.5 }, boundsCenterY: 3.5, boundsRadius: 6.9152 },
    substation_01: { id: "substation_01", category: "industrial", collision: { shape: "box", halfWidth: 6, halfDepth: 5 }, boundsCenterY: 3.5, boundsRadius: 7.4366 },
    highway_sign_01: { id: "highway_sign_01", category: "infrastructure", collision: { shape: "box", halfWidth: 5.8, halfDepth: 0.35 }, boundsCenterY: 3.4, boundsRadius: 6.5177 },
    guardrail_01: { id: "guardrail_01", category: "infrastructure", collision: { shape: "box", halfWidth: 4, halfDepth: 0.22 }, boundsCenterY: 0.465, boundsRadius: 4.028 },
    gas_station_canopy_01: { id: "gas_station_canopy_01", category: "structure", collision: { shape: "none" }, boundsCenterY: 2.6625, boundsRadius: 9.3989 },
    fuel_pump_01: { id: "fuel_pump_01", category: "infrastructure", collision: { shape: "box", halfWidth: 0.55, halfDepth: 0.45 }, boundsCenterY: 1.01, boundsRadius: 1.2099 },
    convenience_store_shell_01: { id: "convenience_store_shell_01", category: "structure", collision: { shape: "box", halfWidth: 7, halfDepth: 5 }, boundsCenterY: 2.6421, boundsRadius: 8.9931 },
    wreck_truck_01: { id: "wreck_truck_01", category: "vehicle", collision: { shape: "box", halfWidth: 1.5, halfDepth: 5.4 }, boundsCenterY: 1.45, boundsRadius: 5.5354 },
    greenhouse_01: { id: "greenhouse_01", category: "agricultural", collision: { shape: "box", halfWidth: 9, halfDepth: 4.5 }, boundsCenterY: 2.7758, boundsRadius: 9.9646 },
    irrigation_pipe_01: { id: "irrigation_pipe_01", category: "agricultural", collision: { shape: "box", halfWidth: 6, halfDepth: 0.35 }, boundsCenterY: 0.305, boundsRadius: 6.0079 },
    farm_water_tank_01: { id: "farm_water_tank_01", category: "agricultural", collision: { shape: "circle", radius: 2.4 }, boundsCenterY: 3.6, boundsRadius: 4.3316 },
    farm_service_building_01: { id: "farm_service_building_01", category: "agricultural", collision: { shape: "box", halfWidth: 6, halfDepth: 4 }, boundsCenterY: 2.6625, boundsRadius: 7.687 },
    crop_row_01: { id: "crop_row_01", category: "agricultural", collision: { shape: "none" }, boundsCenterY: 0.2775, boundsRadius: 6.5421 },
    supply_cache_closed: { id: "supply_cache_closed", category: "infrastructure", collision: { shape: "box", halfWidth: 0.68, halfDepth: 0.42 }, boundsCenterY: 0.34, boundsRadius: 0.8765 },
    supply_cache_open: { id: "supply_cache_open", category: "infrastructure", collision: { shape: "box", halfWidth: 0.68, halfDepth: 0.42 }, boundsCenterY: 0.7081, boundsRadius: 1.0169 },
});
export const AI_ASSET_IDS = Object.freeze([
    "wetware_datacenter_01",
    "ai_compute_structure_01",
    "wetware_vat_01",
    "wetware_node_01",
    "wetware_tissue_cluster_01",
    "wetware_conduit_01",
    "ai_relay_node_01",
    "ai_power_interface_01",
    "ai_sensor_pole_01",
    "ai_ground_sensor_01",
    "autonomous_excavator_01",
    "ai_patrol_drone_01",
]);
export const AI_ASSETS = Object.freeze({
    wetware_datacenter_01: {
        id: "wetware_datacenter_01",
        category: "large_infrastructure",
        collision: { shape: "box", halfWidth: 11, halfDepth: 5.5 },
        boundsCenterY: 3.565,
        boundsRadius: 13.1893,
        sensorCapable: false,
        networked: true,
    },
    ai_compute_structure_01: {
        id: "ai_compute_structure_01",
        category: "large_infrastructure",
        collision: { shape: "box", halfWidth: 4.8, halfDepth: 4.3 },
        boundsCenterY: 9.7072,
        boundsRadius: 12.6258,
        sensorCapable: false,
        networked: true,
    },
    wetware_vat_01: {
        id: "wetware_vat_01",
        category: "wetware",
        collision: { shape: "box", halfWidth: 1.6, halfDepth: 1.4 },
        boundsCenterY: 2.8765,
        boundsRadius: 3.9479,
        sensorCapable: false,
        networked: true,
    },
    wetware_node_01: {
        id: "wetware_node_01",
        category: "wetware",
        collision: { shape: "circle", radius: 1 },
        boundsCenterY: 1.09,
        boundsRadius: 1.8896,
        sensorCapable: false,
        networked: true,
    },
    wetware_tissue_cluster_01: {
        id: "wetware_tissue_cluster_01",
        category: "wetware",
        collision: { shape: "none" },
        boundsCenterY: 1.2424,
        boundsRadius: 2.1195,
        sensorCapable: false,
        networked: false,
    },
    wetware_conduit_01: {
        id: "wetware_conduit_01",
        category: "conduit",
        collision: { shape: "none" },
        boundsCenterY: 0.3366,
        boundsRadius: 3.1357,
        sensorCapable: false,
        networked: false,
    },
    ai_relay_node_01: {
        id: "ai_relay_node_01",
        category: "data_power",
        collision: { shape: "box", halfWidth: 1.25, halfDepth: 1.1 },
        boundsCenterY: 1.7986,
        boundsRadius: 2.64,
        sensorCapable: false,
        networked: true,
    },
    ai_power_interface_01: {
        id: "ai_power_interface_01",
        category: "data_power",
        collision: { shape: "box", halfWidth: 1.65, halfDepth: 1.25 },
        boundsCenterY: 1.56,
        boundsRadius: 2.6978,
        sensorCapable: false,
        networked: true,
    },
    ai_sensor_pole_01: {
        id: "ai_sensor_pole_01",
        category: "sensor",
        collision: { shape: "circle", radius: 0.24 },
        boundsCenterY: 2.7223,
        boundsRadius: 3.1085,
        sensorCapable: true,
        networked: true,
    },
    ai_ground_sensor_01: {
        id: "ai_ground_sensor_01",
        category: "sensor",
        collision: { shape: "none" },
        boundsCenterY: 0.37,
        boundsRadius: 1.0032,
        sensorCapable: true,
        networked: true,
    },
    ai_patrol_drone_01: {
        id: "ai_patrol_drone_01",
        category: "machinery",
        collision: { shape: "none" },
        boundsCenterY: 0.4308,
        boundsRadius: 0.8581,
        sensorCapable: true,
        networked: true,
    },
    autonomous_excavator_01: {
        id: "autonomous_excavator_01",
        category: "machinery",
        collision: { shape: "box", halfWidth: 2.35, halfDepth: 4.25 },
        boundsCenterY: 3.0978,
        boundsRadius: 6.3369,
        sensorCapable: false,
        networked: true,
    },
});
export const TERRAIN_SURFACE = Object.freeze({
    DIRT: 0,
    ASH: 1,
    DARK_SOIL: 2,
    DEAD_GRASS: 3,
    SPARSE_GREEN: 4,
    ROAD: 5,
    RIVERBED: 6,
});
export const TERRAIN_SURFACE_COUNT = 7;
export const ROAD_PATH = Object.freeze({
    controlPoints: Object.freeze([
        { x: -164, z: -96, width: 5.4 },
        { x: -132, z: -72, width: 4.8 },
        { x: -96, z: -51, width: 5.7 },
        { x: -60, z: -31, width: 4.6 },
        { x: -27, z: -7, width: 5.2 },
        { x: 7, z: 18, width: 4.7 },
        { x: 42, z: 35, width: 5.9 },
        { x: 76, z: 55, width: 5.1 },
        { x: 113, z: 66, width: 6.2 },
        { x: 164, z: 97, width: 5.3 },
    ]),
});
/** Stable river depth raised from 0.62 m for surface swimming; terrain/course unchanged. */
export const WORLD_RIVER_MEAN_DEPTH = 1.45;
export const RIVER_PATH = Object.freeze({
    controlPoints: Object.freeze([
        { x: -33, z: -164, width: 9.2, depth: 2.7 },
        { x: -15, z: -130, width: 7.6, depth: 2.45 },
        { x: -25, z: -96, width: 10.4, depth: 3.0 },
        { x: -3, z: -62, width: 8.5, depth: 2.55 },
        { x: 10, z: -29, width: 11.2, depth: 3.1 },
        { x: 8, z: 5, width: 8.2, depth: 2.45 },
        { x: 18, z: 38, width: 10.7, depth: 2.9 },
        { x: 5, z: 71, width: 8.8, depth: 2.6 },
        { x: 18, z: 104, width: 11.5, depth: 3.15 },
        { x: 0, z: 134, width: 8.3, depth: 2.5 },
        { x: 8, z: 164, width: 9.7, depth: 2.8 },
    ]),
    waterDepth: WORLD_RIVER_MEAN_DEPTH,
});
function smooth(value) {
    return value * value * value * (value * (value * 6 - 15) + 10);
}
function mix(start, end, amount) {
    return start + (end - start) * amount;
}
function hashGrid(x, z, seed) {
    let value = Math.imul(x, 374_761_393) + Math.imul(z, 668_265_263) + Math.imul(seed, 1_443_051);
    value = Math.imul(value ^ (value >>> 13), 1_274_126_177);
    return (((value ^ (value >>> 16)) >>> 0) / 4_294_967_295) * 2 - 1;
}
/** Smooth deterministic value noise. Scale is the approximate landform size in world units. */
function coherentNoise(x, z, scale, seed) {
    const sampleX = x / scale;
    const sampleZ = z / scale;
    const cellX = Math.floor(sampleX);
    const cellZ = Math.floor(sampleZ);
    const localX = smooth(sampleX - cellX);
    const localZ = smooth(sampleZ - cellZ);
    const north = mix(hashGrid(cellX, cellZ, seed), hashGrid(cellX + 1, cellZ, seed), localX);
    const south = mix(hashGrid(cellX, cellZ + 1, seed), hashGrid(cellX + 1, cellZ + 1, seed), localX);
    return mix(north, south, localZ);
}
function edgeFade(inner, outer, value) {
    const amount = Math.max(0, Math.min(1, (value - inner) / (outer - inner)));
    return 1 - amount * amount * (3 - amount * 2);
}
function smoothstep(start, end, value) {
    const amount = Math.max(0, Math.min(1, (value - start) / (end - start)));
    return amount * amount * (3 - amount * 2);
}
function catmullRom(previous, start, end, next, amount) {
    const squared = amount * amount;
    const cubed = squared * amount;
    return (0.5 *
        (2 * start +
            (-previous + end) * amount +
            (2 * previous - 5 * start + 4 * end - next) * squared +
            (-previous + 3 * start - 3 * end + next) * cubed));
}
function createCourseSamples(points) {
    const samples = [];
    const subdivisions = 8;
    for (let segment = 0; segment < points.length - 1; segment += 1) {
        const previous = points[Math.max(0, segment - 1)];
        const start = points[segment];
        const end = points[segment + 1];
        const next = points[Math.min(points.length - 1, segment + 2)];
        for (let step = 0; step < subdivisions; step += 1) {
            const amount = step / subdivisions;
            samples.push(Object.freeze({
                x: catmullRom(previous.x, start.x, end.x, next.x, amount),
                z: catmullRom(previous.z, start.z, end.z, next.z, amount),
                width: Math.max(1, catmullRom(previous.width, start.width, end.width, next.width, amount)),
                depth: Math.max(0, catmullRom(previous.depth ?? 0, start.depth ?? 0, end.depth ?? 0, next.depth ?? 0, amount)),
            }));
        }
    }
    const last = points.at(-1);
    samples.push(Object.freeze({ x: last.x, z: last.z, width: last.width, depth: last.depth ?? 0 }));
    return Object.freeze(samples);
}
export const ROAD_COURSE_SAMPLES = createCourseSamples(ROAD_PATH.controlPoints);
export const RIVER_COURSE_SAMPLES = createCourseSamples(RIVER_PATH.controlPoints);
function closestCoursePoint(samples, x, z) {
    let closest = { ...samples[0], distance: Number.POSITIVE_INFINITY };
    for (let index = 0; index < samples.length - 1; index += 1) {
        const start = samples[index];
        const end = samples[index + 1];
        const spanX = end.x - start.x;
        const spanZ = end.z - start.z;
        const lengthSquared = spanX * spanX + spanZ * spanZ;
        const amount = lengthSquared > 0 ? Math.max(0, Math.min(1, ((x - start.x) * spanX + (z - start.z) * spanZ) / lengthSquared)) : 0;
        const centerX = mix(start.x, end.x, amount);
        const centerZ = mix(start.z, end.z, amount);
        const distance = Math.hypot(x - centerX, z - centerZ);
        if (distance < closest.distance) {
            closest = {
                x: centerX,
                z: centerZ,
                width: mix(start.width, end.width, amount),
                depth: mix(start.depth, end.depth, amount),
                distance,
            };
        }
    }
    return closest;
}
/** An elongated landform with tapered ends; used for authored ridges and depressions. */
function ridgeBand(x, z, centerX, centerZ, angle, halfLength, halfWidth, amplitude) {
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    const offsetX = x - centerX;
    const offsetZ = z - centerZ;
    const along = Math.abs(offsetX * cosine + offsetZ * sine);
    const across = Math.abs(-offsetX * sine + offsetZ * cosine);
    const lengthFade = edgeFade(halfLength * 0.66, halfLength, along);
    const widthFade = edgeFade(halfWidth * 0.1, halfWidth, across);
    return amplitude * lengthFade * widthFade * widthFade;
}
/** A restrained one-sided shelf: a broad raised back with a readable eroded face. */
function exposedShelf(x, z, centerX, centerZ, angle, halfLength, halfWidth, amplitude) {
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    const offsetX = x - centerX;
    const offsetZ = z - centerZ;
    const along = offsetX * cosine + offsetZ * sine;
    const across = -offsetX * sine + offsetZ * cosine;
    const lengthFade = edgeFade(halfLength * 0.67, halfLength, Math.abs(along));
    const bodyFade = edgeFade(halfWidth * 0.48, halfWidth, Math.abs(across));
    const exposedFace = smoothstep(-halfWidth * 0.34, halfWidth * 0.08, across);
    return amplitude * lengthFade * bodyFade * (0.22 + exposedFace * 0.78);
}
/** Elliptical damaged-ground landmark with a depressed bowl and a broken low rim. */
function erodedCrater(x, z, centerX, centerZ, radiusX, radiusZ, depth) {
    const dx = (x - centerX) / radiusX;
    const dz = (z - centerZ) / radiusZ;
    const distance = Math.hypot(dx, dz);
    const bowl = -depth * (1 - smoothstep(0.12, 0.78, distance));
    const rim = depth * 0.23 * (smoothstep(0.58, 0.82, distance) - smoothstep(0.82, 1.16, distance));
    const brokenRim = 0.72 + coherentNoise(x + 41, z - 17, 13, 227) * 0.28;
    return bowl + rim * brokenRim;
}
/** Terrain before authored road smoothing and river excavation. */
export function getBaseTerrainHeightAt(x, z) {
    const warpX = coherentNoise(x, z, 74, 11) * 13;
    const warpZ = coherentNoise(x - 31, z + 47, 81, 17) * 13;
    const warpedX = x + warpX;
    const warpedZ = z + warpZ;
    const continental = coherentNoise(warpedX, warpedZ, 112, 23) * 2.8;
    const rolling = coherentNoise(warpedX + 19, warpedZ - 27, 43, 29) * 2.05;
    const brokenGround = coherentNoise(warpedX - 13, warpedZ + 9, 19, 37) * 0.92;
    const fineErosion = coherentNoise(x + 71, z - 53, 8.5, 41) * 0.2;
    const ridgeNoise = coherentNoise(warpedX + 40, warpedZ + 12, 34, 43);
    const erodedRidges = Math.pow(Math.max(0, 1 - Math.abs(ridgeNoise)), 3) * 1.75 - 0.44;
    const authoredForms = ridgeBand(x, z, -27, 34, -0.28, 25, 7.5, 3.7) +
        ridgeBand(x, z, 31, 36, 0.72, 27, 8.5, 3.9) +
        ridgeBand(x, z, -38, -6, 0.18, 24, 7, 3.15) +
        ridgeBand(x, z, 39, -17, 0.42, 28, 8, -2.55) +
        ridgeBand(x, z, -4, -42, -0.76, 25, 7.5, 3.45) +
        ridgeBand(x, z, -76, -39, 0.94, 35, 11, -2.1) +
        ridgeBand(x, z, 88, 69, -0.55, 31, 9, 3.2);
    const phaseThreeGeology = exposedShelf(x, z, -79, 62, -0.32, 18, 7.2, 1.8) +
        exposedShelf(x, z, 72, -58, 0.68, 21, 8.4, 2.1) +
        exposedShelf(x, z, 94, 20, -0.82, 16, 6.8, 1.55) +
        erodedCrater(x, z, -103, -77, 13, 10, 1.5) +
        erodedCrater(x, z, 108, 91, 15, 12, 1.75);
    return continental + rolling + brokenGround + erodedRidges + authoredForms + phaseThreeGeology + fineErosion;
}
export function getRoadInfluenceAt(x, z) {
    const closest = getRoadProfileAt(x, z);
    const irregularity = 1 + coherentNoise(x + 17, z - 29, 10.5, 131) * 0.13;
    const normalizedDistance = closest.distance / Math.max(0.1, closest.width * 0.5 * irregularity);
    return 1 - smoothstep(0.73, 1.13, normalizedDistance);
}
export function getRoadProfileAt(x, z) {
    return closestCoursePoint(ROAD_COURSE_SAMPLES, x, z);
}
export function getRiverProfileAt(x, z) {
    const closest = closestCoursePoint(RIVER_COURSE_SAMPLES, x, z);
    const waterHalfWidth = closest.width * 0.5;
    const bedEdge = waterHalfWidth * 0.82;
    const bankEdge = waterHalfWidth * 1.9;
    return { ...closest, excavation: 1 - smoothstep(bedEdge, bankEdge, closest.distance) };
}
/** Deterministic logical terrain query shared by gameplay and rendering. */
export function getHeightAt(x, z) {
    const baseHeight = getBaseTerrainHeightAt(x, z);
    const road = closestCoursePoint(ROAD_COURSE_SAMPLES, x, z);
    const roadHeight = getBaseTerrainHeightAt(road.x, road.z);
    const roadHeightInfluence = getRoadInfluenceAt(x, z) * 0.14;
    const shapedRoadHeight = mix(baseHeight, roadHeight, roadHeightInfluence);
    const river = getRiverProfileAt(x, z);
    return shapedRoadHeight - river.depth * river.excavation;
}
export function getRiverWaterHeightAt(x, z) {
    const river = closestCoursePoint(RIVER_COURSE_SAMPLES, x, z);
    return getBaseTerrainHeightAt(river.x, river.z) - river.depth + RIVER_PATH.waterDepth;
}
export function getTerrainSurfaceWeights(x, z) {
    const baseHeight = getBaseTerrainHeightAt(x, z);
    const ashField = coherentNoise(x + 61, z - 37, 72, 151) * 0.75 + coherentNoise(x, z, 31, 157) * 0.25;
    const dampField = coherentNoise(x - 27, z + 81, 64, 163) - baseHeight * 0.035;
    const deadField = coherentNoise(x + 101, z + 19, 58, 167) * 0.8 + coherentNoise(x, z, 24, 173) * 0.2;
    const greenField = coherentNoise(x - 83, z - 43, 78, 179) * 0.78 + dampField * 0.22;
    let dirt = 0.72;
    let ash = smoothstep(-0.08, 0.46, ashField) * 1.35;
    let darkSoil = smoothstep(0.02, 0.58, dampField) * 1.1;
    let deadGrass = smoothstep(-0.12, 0.53, deadField) * (1 - ash * 0.28) * 1.18;
    let sparseGreen = smoothstep(0.48, 0.72, greenField) * smoothstep(0.0, 0.48, dampField) * 1.15;
    const sharpen = (value) => value * value;
    dirt = sharpen(dirt);
    ash = sharpen(Math.max(0, ash));
    darkSoil = sharpen(darkSoil);
    deadGrass = sharpen(Math.max(0, deadGrass));
    sparseGreen = sharpen(sparseGreen);
    const regionTotal = dirt + ash + darkSoil + deadGrass + sparseGreen || 1;
    dirt /= regionTotal;
    ash /= regionTotal;
    darkSoil /= regionTotal;
    deadGrass /= regionTotal;
    sparseGreen /= regionTotal;
    const road = getRoadInfluenceAt(x, z);
    const river = getRiverProfileAt(x, z);
    const riverbed = river.excavation;
    const naturalWeight = (1 - road) * (1 - riverbed);
    const roadWeight = road * (1 - riverbed);
    return [
        dirt * naturalWeight,
        ash * naturalWeight,
        darkSoil * naturalWeight,
        deadGrass * naturalWeight,
        sparseGreen * naturalWeight,
        roadWeight,
        riverbed,
    ];
}
export function getTerrainNormalAt(x, z, sampleDistance = 0.5) {
    const dx = getHeightAt(x - sampleDistance, z) - getHeightAt(x + sampleDistance, z);
    const dz = getHeightAt(x, z - sampleDistance) - getHeightAt(x, z + sampleDistance);
    const length = Math.hypot(dx, sampleDistance * 2, dz) || 1;
    return [dx / length, (sampleDistance * 2) / length, dz / length];
}
function geologyRandom(cellX, cellZ, salt) {
    return hashGrid(cellX * 13 + salt * 7, cellZ * 17 - salt * 11, WORLD_SEED + salt) * 0.5 + 0.5;
}
function chooseGeologyAsset(category, variant) {
    if (category === "small")
        return variant < 0.5 ? "rock_small_01" : "rock_small_02";
    if (category === "medium")
        return variant < 0.5 ? "rock_medium_01" : "rock_medium_02";
    if (category === "boulder")
        return variant < 0.5 ? "boulder_large_01" : "boulder_large_02";
    return "rock_formation_01";
}
/** Static world geology. Cell candidates, cluster fields and IDs are deterministic on client and server. */
function createGeologyObjects() {
    const objects = [];
    const cellSize = 8;
    const cellsPerAxis = Math.ceil((START_AREA_HALF_SIZE * 2) / cellSize);
    for (let cellZ = 0; cellZ < cellsPerAxis; cellZ += 1) {
        for (let cellX = 0; cellX < cellsPerAxis; cellX += 1) {
            const x = -START_AREA_HALF_SIZE + (cellX + 0.5) * cellSize + (geologyRandom(cellX, cellZ, 1) - 0.5) * 5.6;
            const z = -START_AREA_HALF_SIZE + (cellZ + 0.5) * cellSize + (geologyRandom(cellX, cellZ, 2) - 0.5) * 5.6;
            const normal = getTerrainNormalAt(x, z, 0.8);
            const slope = 1 - normal[1];
            const elevation = getHeightAt(x, z);
            const road = getRoadInfluenceAt(x, z);
            const roadProfile = getRoadProfileAt(x, z);
            const river = getRiverProfileAt(x, z);
            if (road > 0.035 || river.distance < river.width * 0.56)
                continue;
            const clusterField = coherentNoise(x + 33, z - 71, 37, 239) * 0.68 + coherentNoise(x - 91, z + 17, 19, 241) * 0.32;
            const bankExposure = river.distance < river.width * 2.15 ? 0.18 : 0;
            const exposure = Math.max(0, clusterField * 0.52 + slope * 2.65 + Math.max(0, elevation - 2.5) * 0.018 + bankExposure);
            const density = 0.03 + Math.min(0.18, exposure * 0.135);
            if (geologyRandom(cellX, cellZ, 3) > density)
                continue;
            const classRoll = geologyRandom(cellX, cellZ, 4);
            let category;
            if (classRoll > 0.93)
                category = "formation";
            else if (classRoll > 0.74 || (classRoll > 0.67 && slope > 0.11))
                category = "boulder";
            else if (classRoll > 0.38)
                category = "medium";
            else
                category = "small";
            const assetId = chooseGeologyAsset(category, geologyRandom(cellX, cellZ, 5));
            const definition = GEOLOGY_ASSETS[assetId];
            const scaleVariation = 0.84 + geologyRandom(cellX, cellZ, 6) * 0.36;
            const scale = category === "formation" ? 0.92 + geologyRandom(cellX, cellZ, 6) * 0.22 : scaleVariation;
            const collisionRadius = definition.collisionRadius * scale;
            if (collisionRadius > 0) {
                const clearance = collisionRadius + PLAYER_RADIUS + 0.35;
                const touchesRoad = roadProfile.distance < roadProfile.width * 0.65 + clearance;
                if (touchesRoad || river.distance < river.width * 0.5 + clearance)
                    continue;
            }
            const embedding = category === "formation" ? 0.2 : category === "boulder" ? 0.1 : category === "medium" ? 0.045 : 0.025;
            objects.push(Object.freeze({
                id: `geology:${cellX}:${cellZ}`,
                assetId,
                category,
                x,
                y: getHeightAt(x, z) - embedding * scale,
                z,
                scale,
                yaw: geologyRandom(cellX, cellZ, 7) * Math.PI * 2,
                terrainNormal: normal,
                normalAlignment: category === "formation" ? 0.18 : category === "boulder" ? 0.32 : 0.45,
                tint: (geologyRandom(cellX, cellZ, 8) - 0.5) * 0.13,
                collisionRadius,
                chunkX: Math.floor(x / TERRAIN_CHUNK_SIZE),
                chunkZ: Math.floor(z / TERRAIN_CHUNK_SIZE),
            }));
        }
    }
    return Object.freeze(objects);
}
export const GEOLOGY_OBJECTS = createGeologyObjects();
function vegetationRandom(cellX, cellZ, salt) {
    return hashGrid(cellX * 19 - salt * 5, cellZ * 23 + salt * 13, WORLD_SEED + 1_009 + salt) * 0.5 + 0.5;
}
function chooseVegetationAsset(cellX, cellZ, surface, slope) {
    const structureRoll = vegetationRandom(cellX, cellZ, 8);
    if (structureRoll < 0.16 + Math.min(0.1, slope * 0.45)) {
        return vegetationRandom(cellX, cellZ, 9) < 0.54 ? "shrub_dead_01" : "shrub_dead_02";
    }
    const ash = surface[TERRAIN_SURFACE.ASH];
    if (ash > 0.3 && vegetationRandom(cellX, cellZ, 10) < 0.72) {
        return vegetationRandom(cellX, cellZ, 11) < 0.64 ? "tree_burned_01" : "tree_broken_01";
    }
    const dampSurvival = surface[TERRAIN_SURFACE.DARK_SOIL] * 0.35 + surface[TERRAIN_SURFACE.SPARSE_GREEN];
    if (dampSurvival > 0.16 && vegetationRandom(cellX, cellZ, 12) < Math.min(0.42, dampSurvival * 0.82)) {
        return "tree_sparse_01";
    }
    const deadRoll = vegetationRandom(cellX, cellZ, 13);
    if (deadRoll < 0.31)
        return "tree_dead_01";
    if (deadRoll < 0.59)
        return "tree_dead_02";
    if (deadRoll < 0.84)
        return "tree_dead_03";
    return "tree_broken_01";
}
/** Deterministic damaged vegetation driven by surface, slope, water, roads, geology and broad cluster fields. */
function createVegetationObjects() {
    const objects = [];
    const cellSize = 5.75;
    const cellsPerAxis = Math.floor((START_AREA_HALF_SIZE * 2) / cellSize);
    for (let cellZ = 0; cellZ < cellsPerAxis; cellZ += 1) {
        for (let cellX = 0; cellX < cellsPerAxis; cellX += 1) {
            const x = -START_AREA_HALF_SIZE + (cellX + 0.5) * cellSize + (vegetationRandom(cellX, cellZ, 1) - 0.5) * 3.8;
            const z = -START_AREA_HALF_SIZE + (cellZ + 0.5) * cellSize + (vegetationRandom(cellX, cellZ, 2) - 0.5) * 3.8;
            const clusterField = coherentNoise(x - 44, z + 21, 51, 307) * 0.72 + coherentNoise(x + 73, z - 61, 23, 311) * 0.28;
            const clusterAmount = smoothstep(-0.24, 0.62, clusterField);
            const surface = getTerrainSurfaceWeights(x, z);
            const damagedGround = surface[TERRAIN_SURFACE.DIRT] * 0.88 +
                surface[TERRAIN_SURFACE.ASH] * 1.18 +
                surface[TERRAIN_SURFACE.DARK_SOIL] * 0.72 +
                surface[TERRAIN_SURFACE.DEAD_GRASS] * 1.08 +
                surface[TERRAIN_SURFACE.SPARSE_GREEN] * 0.48;
            const density = (0.036 + clusterAmount * clusterAmount * 0.35) * damagedGround;
            if (vegetationRandom(cellX, cellZ, 3) > density)
                continue;
            const normal = getTerrainNormalAt(x, z, 0.8);
            const slope = 1 - normal[1];
            const assetId = chooseVegetationAsset(cellX, cellZ, surface, slope);
            const definition = VEGETATION_ASSETS[assetId];
            const shrub = definition.category === "dead_shrub";
            if ((!shrub && normal[1] < 0.88) || (shrub && normal[1] < 0.74))
                continue;
            const scale = shrub
                ? 0.76 + vegetationRandom(cellX, cellZ, 4) * 0.48
                : 0.82 + vegetationRandom(cellX, cellZ, 4) * 0.36;
            const collisionRadius = definition.collisionRadius * scale;
            const objectClearance = (collisionRadius || definition.boundsRadius * scale * 0.18) + 0.45;
            const road = getRoadProfileAt(x, z);
            const river = getRiverProfileAt(x, z);
            if (road.distance < road.width * 0.68 + objectClearance)
                continue;
            if (river.distance < river.width * 0.52 + objectClearance)
                continue;
            if (Math.hypot(x, z - 12) < 3.2 + collisionRadius)
                continue;
            let overlapsGeology = false;
            for (const geology of GEOLOGY_OBJECTS) {
                if (geology.category !== "boulder" && geology.category !== "formation")
                    continue;
                const geologyClearance = GEOLOGY_ASSETS[geology.assetId].boundsRadius * geology.scale + objectClearance;
                if (Math.hypot(x - geology.x, z - geology.z) < geologyClearance) {
                    overlapsGeology = true;
                    break;
                }
            }
            if (overlapsGeology)
                continue;
            objects.push(Object.freeze({
                id: `vegetation:${cellX}:${cellZ}`,
                assetId,
                category: definition.category,
                x,
                y: getHeightAt(x, z) - (shrub ? 0.015 : 0.035) * scale,
                z,
                scale,
                yaw: vegetationRandom(cellX, cellZ, 5) * Math.PI * 2,
                tint: (vegetationRandom(cellX, cellZ, 6) - 0.5) * 0.1,
                collisionRadius,
                chunkX: Math.floor(x / TERRAIN_CHUNK_SIZE),
                chunkZ: Math.floor(z / TERRAIN_CHUNK_SIZE),
            }));
        }
    }
    return Object.freeze(objects);
}
export const VEGETATION_OBJECTS = createVegetationObjects();
export const HUMAN_SITES = Object.freeze([
    Object.freeze({
        id: "human-site:abandoned-checkpoint:01",
        type: "abandoned_checkpoint",
        x: -12,
        z: 5,
        rotation: 0.94,
        radius: 31,
    }),
]);
const HUMAN_SITE_BLUEPRINTS = Object.freeze({
    abandoned_checkpoint: [
        // Modular roadside shelter and collapsed service building.
        { assetId: "small_bunker_01", localX: -12, localZ: 9, localYaw: 0.16, scale: 1, embedding: 0.34, terrainAlignment: 0 },
        { assetId: "ruined_concrete_wall_01", localX: -10.8, localZ: 0.5, localYaw: 0.1, scale: 1, embedding: 0.08, terrainAlignment: 0 },
        { assetId: "ruined_concrete_wall_02", localX: -13.1, localZ: 3.2, localYaw: Math.PI * 0.5, scale: 0.96, embedding: 0.08, terrainAlignment: 0 },
        { assetId: "ruined_corner_01", localX: -13.6, localZ: 0.7, localYaw: 0.04, scale: 0.92, embedding: 0.08, terrainAlignment: 0 },
        { assetId: "ruined_entry_01", localX: -14, localZ: -1.7, localYaw: 0.08, scale: 0.94, embedding: 0.07, terrainAlignment: 0 },
        { assetId: "collapsed_slab_01", localX: -10.7, localZ: 3.8, localYaw: -0.18, scale: 1, embedding: 0.04, terrainAlignment: 0.4 },
        { assetId: "collapsed_slab_01", localX: -14.2, localZ: -3.7, localYaw: 0.36, scale: 0.78, embedding: 0.03, terrainAlignment: 0.5 },
        // Sparse road corridor infrastructure; the center lane remains open.
        { assetId: "lamp_post_broken_01", localX: -5, localZ: -4.5, localYaw: 0.12, scale: 0.94, embedding: 0.07, terrainAlignment: 0 },
        { assetId: "lamp_post_broken_01", localX: -5, localZ: 13, localYaw: -0.14, scale: 1.04, embedding: 0.07, terrainAlignment: 0 },
        { assetId: "utility_pole_01", localX: 6.6, localZ: -17, localYaw: -0.08, scale: 0.94, embedding: 0.13, terrainAlignment: 0 },
        { assetId: "utility_pole_01", localX: 6.4, localZ: -4.2, localYaw: 0.12, scale: 1.03, embedding: 0.14, terrainAlignment: 0 },
        { assetId: "utility_pole_01", localX: 6.2, localZ: 13, localYaw: -0.04, scale: 0.98, embedding: 0.14, terrainAlignment: 0 },
        { assetId: "utility_pole_01", localX: -6, localZ: 23, localYaw: 0.1, scale: 0.9, embedding: 0.14, terrainAlignment: 0 },
        { assetId: "road_sign_01", localX: 4.2, localZ: 14, localYaw: Math.PI, scale: 0.92, embedding: 0.08, terrainAlignment: 0 },
        { assetId: "road_sign_01", localX: -4.1, localZ: -13.5, localYaw: 0.08, scale: 0.83, embedding: 0.08, terrainAlignment: 0 },
        { assetId: "concrete_barrier_01", localX: -4.1, localZ: 4.3, localYaw: 0.04, scale: 1, embedding: 0.03, terrainAlignment: 0.45 },
        { assetId: "concrete_barrier_01", localX: 4.1, localZ: 4.3, localYaw: -0.05, scale: 0.96, embedding: 0.03, terrainAlignment: 0.45 },
        { assetId: "concrete_barrier_01", localX: -4.2, localZ: 17.2, localYaw: -0.08, scale: 0.88, embedding: 0.03, terrainAlignment: 0.45 },
        { assetId: "concrete_barrier_01", localX: 4.3, localZ: 17.2, localYaw: 0.07, scale: 0.9, embedding: 0.03, terrainAlignment: 0.45 },
        // Abandoned industrial side yard.
        { assetId: "wreck_car_01", localX: 4, localZ: 8.2, localYaw: 0.11, scale: 1, embedding: 0.16, terrainAlignment: 0.16 },
        { assetId: "industrial_tank_01", localX: 10, localZ: 8, localYaw: 0.08, scale: 1, embedding: 0.42, terrainAlignment: 0.08 },
        { assetId: "fence_segment_01", localX: 10, localZ: 2.2, localYaw: 0, scale: 1, embedding: 0.08, terrainAlignment: 0 },
        { assetId: "fence_segment_01", localX: 13.2, localZ: 4.9, localYaw: Math.PI * 0.5, scale: 0.9, embedding: 0.08, terrainAlignment: 0 },
        { assetId: "fence_segment_01", localX: 13.1, localZ: 10, localYaw: Math.PI * 0.5 + 0.06, scale: 0.86, embedding: 0.08, terrainAlignment: 0 },
        { assetId: "fence_segment_01", localX: -17, localZ: 1, localYaw: Math.PI * 0.5, scale: 0.82, embedding: 0.08, terrainAlignment: 0 },
        { assetId: "fence_segment_01", localX: -16, localZ: 10, localYaw: Math.PI * 0.5 - 0.08, scale: 0.7, embedding: 0.08, terrainAlignment: 0 },
        // Debris is concentrated around structures instead of scattered through wilderness.
        { assetId: "concrete_rubble_01", localX: -5.8, localZ: 1.5, localYaw: 0.3, scale: 0.92, embedding: 0.02, terrainAlignment: 0.72 },
        { assetId: "concrete_rubble_02", localX: -11, localZ: 1.7, localYaw: -0.4, scale: 1.08, embedding: 0.02, terrainAlignment: 0.68 },
        { assetId: "concrete_rubble_01", localX: -7.5, localZ: 1, localYaw: -0.2, scale: 0.75, embedding: 0.02, terrainAlignment: 0.75 },
        { assetId: "concrete_rubble_02", localX: -14.1, localZ: -1.2, localYaw: 0.5, scale: 0.88, embedding: 0.02, terrainAlignment: 0.7 },
        { assetId: "concrete_rubble_01", localX: -11.8, localZ: 5.4, localYaw: 0.1, scale: 0.64, embedding: 0.02, terrainAlignment: 0.78 },
        { assetId: "concrete_rubble_02", localX: 7.4, localZ: 5, localYaw: -0.3, scale: 0.72, embedding: 0.02, terrainAlignment: 0.72 },
        { assetId: "concrete_rubble_01", localX: 11.7, localZ: 12, localYaw: 0.44, scale: 0.82, embedding: 0.02, terrainAlignment: 0.72 },
        { assetId: "metal_debris_01", localX: -9.6, localZ: 1.7, localYaw: 0.2, scale: 0.9, embedding: 0.02, terrainAlignment: 0.62 },
        { assetId: "metal_debris_01", localX: 7.7, localZ: 8.6, localYaw: -0.5, scale: 0.74, embedding: 0.02, terrainAlignment: 0.65 },
        { assetId: "metal_debris_01", localX: 11.3, localZ: 5.5, localYaw: 0.35, scale: 0.8, embedding: 0.02, terrainAlignment: 0.65 },
        // Thin terrain-aligned patches preserve the Phase 2 road mesh.
        { assetId: "road_damage_01", localX: 0.2, localZ: -12, localYaw: 0.2, scale: 0.8, embedding: -0.025, terrainAlignment: 0.96 },
        { assetId: "road_damage_01", localX: -0.4, localZ: -4.4, localYaw: -0.32, scale: 0.68, embedding: -0.025, terrainAlignment: 0.96 },
        { assetId: "road_damage_01", localX: 0.3, localZ: 5.6, localYaw: 0.12, scale: 0.9, embedding: -0.025, terrainAlignment: 0.96 },
        { assetId: "road_damage_01", localX: -0.3, localZ: 13, localYaw: -0.18, scale: 0.74, embedding: -0.025, terrainAlignment: 0.96 },
        { assetId: "road_damage_01", localX: 0.45, localZ: 21, localYaw: 0.36, scale: 0.62, embedding: -0.025, terrainAlignment: 0.96 },
    ],
});
function createHumanObjects() {
    const objects = [];
    for (let siteIndex = 0; siteIndex < HUMAN_SITES.length; siteIndex += 1) {
        const site = HUMAN_SITES[siteIndex];
        const cosine = Math.cos(site.rotation);
        const sine = Math.sin(site.rotation);
        const blueprint = HUMAN_SITE_BLUEPRINTS[site.type];
        for (let objectIndex = 0; objectIndex < blueprint.length; objectIndex += 1) {
            const placed = blueprint[objectIndex];
            const x = site.x + placed.localX * cosine + placed.localZ * sine;
            const z = site.z - placed.localX * sine + placed.localZ * cosine;
            const definition = HUMAN_ASSETS[placed.assetId];
            objects.push(Object.freeze({
                id: `${site.id}:object:${objectIndex}`,
                siteId: site.id,
                assetId: placed.assetId,
                category: definition.category,
                x,
                y: getHeightAt(x, z) - placed.embedding * placed.scale,
                z,
                yaw: site.rotation + placed.localYaw,
                scale: placed.scale,
                tint: (hashGrid(siteIndex * 97 + objectIndex, objectIndex * 31 - siteIndex, WORLD_SEED + 6_001) * 0.5) * 0.065,
                terrainNormal: getTerrainNormalAt(x, z, 0.7),
                terrainAlignment: placed.terrainAlignment,
                chunkX: Math.floor(x / TERRAIN_CHUNK_SIZE),
                chunkZ: Math.floor(z / TERRAIN_CHUNK_SIZE),
            }));
        }
    }
    return Object.freeze(objects);
}
export const HUMAN_OBJECTS = createHumanObjects();
export const AI_SITES = Object.freeze([
    Object.freeze({
        id: "ai-site:wetware-relay:01",
        type: "wetware_relay",
        x: 28,
        z: -48,
        rotation: -0.55,
        radius: 23,
        state: "active",
    }),
]);
const AI_SITE_BLUEPRINTS = Object.freeze({
    wetware_relay: [
        // Human-built anchor. A failed +Z/+X service corner reveals the compact biological equipment inside.
        { assetId: "wetware_datacenter_01", localX: 7, localZ: 2, localYaw: -0.1, scale: 1, embedding: 0.05, terrainAlignment: 0 },
        { assetId: "ai_compute_structure_01", localX: 12.6, localZ: 3.4, localYaw: -0.1, scale: 0.31, embedding: 0.06, terrainAlignment: 0 },
        { assetId: "wetware_vat_01", localX: 15.75, localZ: 1.3, localYaw: -0.18, scale: 0.72, embedding: 0.08, terrainAlignment: 0 },
        { assetId: "wetware_vat_01", localX: 15.65, localZ: 5.35, localYaw: 0.02, scale: 0.68, embedding: 0.07, terrainAlignment: 0 },
        { assetId: "wetware_node_01", localX: 10.15, localZ: 5.45, localYaw: 0.08, scale: 0.84, embedding: 0.06, terrainAlignment: 0 },
        { assetId: "wetware_tissue_cluster_01", localX: 14.1, localZ: 3.72, localYaw: -0.22, scale: 0.68, embedding: 0.035, terrainAlignment: 0.08 },
        // Four sparse ground runs explain building -> relay -> power -> sensor connectivity.
        { assetId: "wetware_conduit_01", localX: 0.2, localZ: -6.25, localYaw: Math.PI * 0.5, scale: 0.9, embedding: -0.025, terrainAlignment: 0.94 },
        { assetId: "wetware_conduit_01", localX: 10, localZ: -6.25, localYaw: Math.PI * 0.5, scale: 0.9, embedding: -0.025, terrainAlignment: 0.94 },
        { assetId: "wetware_conduit_01", localX: 5, localZ: -9.15, localYaw: 0, scale: 1.48, embedding: -0.025, terrainAlignment: 0.94 },
        { assetId: "wetware_conduit_01", localX: 14, localZ: -9.4, localYaw: 0, scale: 1.2, embedding: -0.025, terrainAlignment: 0.94 },
        // Original utility hardware remains outside the shell; AI additions are practical retrofits.
        { assetId: "ai_relay_node_01", localX: 0, localZ: -9.4, localYaw: 0.12, scale: 0.92, embedding: 0.09, terrainAlignment: 0 },
        { assetId: "ai_power_interface_01", localX: 10, localZ: -9.4, localYaw: -0.1, scale: 0.94, embedding: 0.11, terrainAlignment: 0 },
        // CCTV-scale perimeter observation replaces the previous ring of turret-like silhouettes.
        { assetId: "ai_sensor_pole_01", localX: -10, localZ: -10, localYaw: 0.2, scale: 0.9, embedding: 0.1, terrainAlignment: 0 },
        { assetId: "ai_sensor_pole_01", localX: 17, localZ: -10, localYaw: -0.16, scale: 0.92, embedding: 0.1, terrainAlignment: 0 },
        { assetId: "ai_sensor_pole_01", localX: 15, localZ: 10, localYaw: 0.18, scale: 0.86, embedding: 0.09, terrainAlignment: 0 },
        { assetId: "ai_ground_sensor_01", localX: 17, localZ: -3.2, localYaw: 0.12, scale: 0.9, embedding: 0.025, terrainAlignment: 0.82 },
    ],
});
function createAIObjects() {
    const objects = [];
    for (let siteIndex = 0; siteIndex < AI_SITES.length; siteIndex += 1) {
        const site = AI_SITES[siteIndex];
        const cosine = Math.cos(site.rotation);
        const sine = Math.sin(site.rotation);
        const blueprint = AI_SITE_BLUEPRINTS[site.type];
        for (let objectIndex = 0; objectIndex < blueprint.length; objectIndex += 1) {
            const placed = blueprint[objectIndex];
            const x = site.x + placed.localX * cosine + placed.localZ * sine;
            const z = site.z - placed.localX * sine + placed.localZ * cosine;
            const definition = AI_ASSETS[placed.assetId];
            objects.push(Object.freeze({
                id: `${site.id}:object:${objectIndex}`,
                siteId: site.id,
                assetId: placed.assetId,
                category: definition.category,
                x,
                y: getHeightAt(x, z) - placed.embedding * placed.scale,
                z,
                yaw: site.rotation + placed.localYaw,
                scale: placed.scale,
                tint: hashGrid(siteIndex * 113 + objectIndex, objectIndex * 43 - siteIndex, WORLD_SEED + 7_001) * 0.018,
                terrainNormal: getTerrainNormalAt(x, z, 0.7),
                terrainAlignment: placed.terrainAlignment,
                collision: definition.collision,
                active: site.state === "active",
                sensorCapable: definition.sensorCapable,
                networked: definition.networked,
                chunkX: Math.floor(x / TERRAIN_CHUNK_SIZE),
                chunkZ: Math.floor(z / TERRAIN_CHUNK_SIZE),
            }));
        }
    }
    return Object.freeze(objects);
}
export const AI_OBJECTS = createAIObjects();
const STATIC_COLLIDERS = Object.freeze([
    ...GEOLOGY_OBJECTS.filter((object) => object.collisionRadius > 0).map((object) => Object.freeze({
        id: object.id,
        category: "geology",
        x: object.x,
        z: object.z,
        shape: "circle",
        radius: object.collisionRadius,
        fallbackAngle: object.yaw,
    })),
    ...VEGETATION_OBJECTS.filter((object) => object.collisionRadius > 0).map((object) => Object.freeze({
        id: object.id,
        category: "tree",
        x: object.x,
        z: object.z,
        shape: "circle",
        radius: object.collisionRadius,
        fallbackAngle: object.yaw,
    })),
    ...HUMAN_OBJECTS.map(createHumanCollider).filter((collider) => collider !== undefined),
    ...AI_OBJECTS.map(createAICollider).filter((collider) => collider !== undefined),
]);
function createHumanCollider(object) {
    const collision = HUMAN_ASSETS[object.assetId].collision;
    const base = {
        id: object.id,
        category: "human",
        x: object.x,
        z: object.z,
        fallbackAngle: object.yaw,
    };
    if (collision.shape === "none")
        return undefined;
    if (collision.shape === "circle") {
        return Object.freeze({ ...base, shape: "circle", radius: collision.radius * object.scale });
    }
    if (collision.shape === "box") {
        return Object.freeze({
            ...base,
            shape: "box",
            halfWidth: collision.halfWidth * object.scale,
            halfDepth: collision.halfDepth * object.scale,
            yaw: object.yaw,
        });
    }
    return Object.freeze({
        ...base,
        shape: "capsule",
        radius: collision.radius * object.scale,
        halfLength: collision.halfLength * object.scale,
        yaw: object.yaw,
    });
}
function createAICollider(object) {
    const collision = object.collision;
    const base = {
        id: object.id,
        category: "ai",
        x: object.x,
        z: object.z,
        fallbackAngle: object.yaw,
    };
    if (collision.shape === "none")
        return undefined;
    if (collision.shape === "circle") {
        return Object.freeze({ ...base, shape: "circle", radius: collision.radius * object.scale });
    }
    if (collision.shape === "box") {
        return Object.freeze({
            ...base,
            shape: "box",
            halfWidth: collision.halfWidth * object.scale,
            halfDepth: collision.halfDepth * object.scale,
            yaw: object.yaw,
        });
    }
    return Object.freeze({
        ...base,
        shape: "capsule",
        radius: collision.radius * object.scale,
        halfLength: collision.halfLength * object.scale,
        yaw: object.yaw,
    });
}
const COLLISION_GRID = (() => {
    const mutable = new Map();
    for (const collider of STATIC_COLLIDERS) {
        const key = `${Math.floor(collider.x / TERRAIN_CHUNK_SIZE)}:${Math.floor(collider.z / TERRAIN_CHUNK_SIZE)}`;
        const cell = mutable.get(key);
        if (cell)
            cell.push(collider);
        else
            mutable.set(key, [collider]);
    }
    const grid = new Map();
    for (const [key, colliders] of mutable)
        grid.set(key, Object.freeze(colliders));
    return grid;
})();
/** Test/debug query. Runtime collision visits these cells directly without creating a candidate array. */
export function getCollisionBroadphaseCandidatesAt(x, z) {
    const cellX = Math.floor(x / TERRAIN_CHUNK_SIZE);
    const cellZ = Math.floor(z / TERRAIN_CHUNK_SIZE);
    const candidates = [];
    for (let offsetZ = -1; offsetZ <= 1; offsetZ += 1) {
        for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
            const cell = COLLISION_GRID.get(`${cellX + offsetX}:${cellZ + offsetZ}`);
            if (cell)
                candidates.push(...cell);
        }
    }
    return candidates;
}
export function getStaticColliderCount() {
    return STATIC_COLLIDERS.length;
}
export function resolveWorldCollision(targetX, targetZ, probe) {
    const limit = WORLD_HALF_SIZE - PLAYER_RADIUS;
    let x = Math.max(-limit, Math.min(limit, targetX));
    let z = Math.max(-limit, Math.min(limit, targetZ));
    const initialX = x;
    const initialZ = z;
    let converged = false;
    if (probe) {
        probe.candidateCount = 0;
        delete probe.contact;
    }
    for (let pass = 0; pass < 8; pass += 1) {
        let resolvedAny = false;
        const cellX = Math.floor(x / TERRAIN_CHUNK_SIZE);
        const cellZ = Math.floor(z / TERRAIN_CHUNK_SIZE);
        for (let offsetZ = -1; offsetZ <= 1; offsetZ += 1) {
            for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
                const colliders = COLLISION_GRID.get(`${cellX + offsetX}:${cellZ + offsetZ}`);
                if (!colliders)
                    continue;
                for (const collider of colliders) {
                    if (probe)
                        probe.candidateCount += 1;
                    const [resolvedX, resolvedZ] = resolveStaticCollider(x, z, collider);
                    const movementX = resolvedX - x;
                    const movementZ = resolvedZ - z;
                    if (movementX * movementX + movementZ * movementZ > 0.000000000001) {
                        resolvedAny = true;
                        if (probe && !probe.contact) {
                            const distance = Math.hypot(movementX, movementZ);
                            probe.contact = { normalX: movementX / distance, normalZ: movementZ / distance, obstacleId: collider.id, category: collider.category };
                        }
                    }
                    x = resolvedX;
                    z = resolvedZ;
                }
            }
        }
        if (!resolvedAny) {
            converged = true;
            break;
        }
    }
    if (!converged) {
        [x, z] = findCollisionFreePoint(initialX, initialZ, x, z, limit, probe);
    }
    return [Math.max(-limit, Math.min(limit, x)), Math.max(-limit, Math.min(limit, z))];
}
/** Bounded deterministic escape for touching modular proxies whose expanded bounds overlap. */
function findCollisionFreePoint(originX, originZ, iterativeX, iterativeZ, worldLimit, probe) {
    const escapeAngle = Math.atan2(iterativeZ - originZ, iterativeX - originX);
    const baseAngle = Number.isFinite(escapeAngle) ? escapeAngle : 0;
    const directionCount = 32;
    const radiusStep = 0.12;
    const maximumRadius = 12;
    for (let radius = radiusStep; radius <= maximumRadius; radius += radiusStep) {
        for (let direction = 0; direction < directionCount; direction += 1) {
            const angle = baseAngle + (direction / directionCount) * Math.PI * 2;
            const candidateX = originX + Math.cos(angle) * radius;
            const candidateZ = originZ + Math.sin(angle) * radius;
            if (Math.abs(candidateX) > worldLimit || Math.abs(candidateZ) > worldLimit)
                continue;
            if (!hasStaticCollisionAt(candidateX, candidateZ, probe))
                return [candidateX, candidateZ];
        }
    }
    return [iterativeX, iterativeZ];
}
function hasStaticCollisionAt(x, z, probe) {
    const cellX = Math.floor(x / TERRAIN_CHUNK_SIZE);
    const cellZ = Math.floor(z / TERRAIN_CHUNK_SIZE);
    for (let offsetZ = -1; offsetZ <= 1; offsetZ += 1) {
        for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
            const colliders = COLLISION_GRID.get(`${cellX + offsetX}:${cellZ + offsetZ}`);
            if (!colliders)
                continue;
            for (const collider of colliders) {
                if (probe)
                    probe.candidateCount += 1;
                if (hasStaticColliderCollisionAt(x, z, collider))
                    return true;
            }
        }
    }
    return false;
}
function hasStaticColliderCollisionAt(x, z, collider) {
    if (collider.shape === "circle") {
        const minimumDistance = collider.radius + PLAYER_RADIUS - 0.000001;
        const offsetX = x - collider.x;
        const offsetZ = z - collider.z;
        return offsetX * offsetX + offsetZ * offsetZ < minimumDistance * minimumDistance;
    }
    const cosine = Math.cos(collider.yaw);
    const sine = Math.sin(collider.yaw);
    const offsetX = x - collider.x;
    const offsetZ = z - collider.z;
    const localX = offsetX * cosine - offsetZ * sine;
    const localZ = offsetX * sine + offsetZ * cosine;
    if (collider.shape === "capsule") {
        const axisX = Math.max(-collider.halfLength, Math.min(collider.halfLength, localX));
        const differenceX = localX - axisX;
        const minimumDistance = collider.radius + PLAYER_RADIUS - 0.000001;
        return differenceX * differenceX + localZ * localZ < minimumDistance * minimumDistance;
    }
    const closestX = Math.max(-collider.halfWidth, Math.min(collider.halfWidth, localX));
    const closestZ = Math.max(-collider.halfDepth, Math.min(collider.halfDepth, localZ));
    const differenceX = localX - closestX;
    const differenceZ = localZ - closestZ;
    if (differenceX === 0 && differenceZ === 0)
        return true;
    const minimumDistance = PLAYER_RADIUS - 0.000001;
    return differenceX * differenceX + differenceZ * differenceZ < minimumDistance * minimumDistance;
}
